<template>
  <v-container fluid grid-list-md text-xs-left class="gridContainer px-0 py-0 mx-0 my-0">
    <v-layout row wrap>
      <v-flex d-flex xs12>
        <ticket-form
          :dealId="dealModel.dealId"
          :productCode="dealModel.productCode"
          :clientId="dealModel.clientId"
          :origin="'OTC'"
          v-model="contractFormVisible"
        />
        <div style="width: 100%">
          <schedule-delivery-form
            v-if="scheduleModalVisible"
            :operation-id="operationId"
            :is-read-only="isReadOnly"
            :schedule-modal-visible="scheduleModalVisible"
            @close="closeScheduleDeliveryModal"
          />
          <range-deals
            v-model="addRangeDealsDialog"
            @datesSelected="onRangeDatesSelected"
            :initial-start-date="initialStartDate"
          />

          <page-toolbar class="toolbar-dark">
            <kendo-button icon="add" title="Add Trade" v-if="editable" @click="addRowClick">
            </kendo-button>
            <kendo-button
              icon="cancel"
              title="Cancel Changes"
              v-if="editable"
              @click="cancelGridChanges"
            >
            </kendo-button>
            <kendo-button
              icon="copy"
              title="Duplicate"
              v-if="editable"
              @click="openRangeDealsDialog"
            >
            </kendo-button>
            <kendo-button
              icon="eye"
              :title="showAllColumns ? 'Show less' : 'Show All'"
              @click="showAllClick"
            >
            </kendo-button>
            <kendo-button
              icon="excel"
              title="Export Ticket"
              @click="exportToExcel"
              v-if="clientName !== 'Heroux'"
            >
            </kendo-button>
            <kendo-button
              icon="excel"
              title="Export Ticket"
              @click="generateTicket"
              v-if="clientName === 'Heroux'"
            >
            </kendo-button>
            <kendo-button
              icon="window"
              title="FX Ticket"
              @click="openTicketForm"
              v-if="clientName !== 'Heroux'"
            >
            </kendo-button>
          </page-toolbar>
          <div :key="operationsGridHeight">
            <kendo-grid
              ref="operationGrid"
              :data-source="this.operationDataSource"
              style="width:100%;height:100%;font-size:82%"
              :height="this.operationsGridHeight"
              :editable="editableSettings"
              :navigatable="true"
              :filterable="false"
              :pageable="false"
              :sortable="false"
              :scrollable="true"
              :pageable-always-visible="true"
              :columns="gridColumns"
            ></kendo-grid>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import {mapGetters} from 'vuex'
import ScheduleDeliveryForm from './ScheduleDeliveryForm'
import PageToolbar from '@/components/PageToolbar'
import RangeDeals from './RangeDeals'
import params from '@/helpers/params'
import {
  jsonDropDownEditor,
  datePickerEditor,
  readOnlyEditor,
  numericEditor,
} from '@/helpers/kendo-editors'
import {getDataItem} from '@/helpers/kendo-helpers'
import {product} from '@/helpers/enums'
import {isBuyingEditor, baseCcyEditor} from './editors'
import {max, addMonths} from 'date-fns'
import TicketForm from '@/components/TicketForm'
import confirm from '@/helpers/confirmation-window'

const $ = kendo.jQuery

export default {
  inject: ['EventBus'],
  beforeDestroy() {
    document.body.removeEventListener('click', this.openScheduleDeliveryModal)
  },
  mounted() {
    this.EventBus.$on('addOperationsFromOrder', this.onAddOperationsFromOrder)
    document.body.addEventListener('click', this.openScheduleDeliveryModal, false)
    this.recalculateGridHeight()
  },
  props: {
    dealModel: {type: Object, required: true},
    isReadOnly: {type: Boolean, required: true},
    editable: {type: Boolean, required: true},
    hasOrders: {type: Boolean, required: true},
    dealTypeId: {type: Number, required: true},
    marketId: {type: Number, required: true},
  },
  data: () => ({
    showAllColumns: true,
    optionProducts: [product.Collar, product.Exotic, product.Call, product.Put],
    addRangeDealsDialog: false,
    scheduleModalVisible: false,
    operationId: null,
    operationsGridOffsetTop: 0,
    lastStartDate: null,
    contractFormVisible: false,
    addTradeByOrder: function() {
      return (
        this.dealModel.dealId != 0 && this.dealModel.order && this.dealModel.operations.length == 0
      )
    },
  }),
  watch: {
    'dealModel.tradeDate': function tradeDateChange(newValue) {
      const date = kendo.parseDate(newValue, 'yyyy-MM-dd')
      if (!date) return
      this.operationDataSource.data().forEach(dataItem => {
        if (dataItem.maturityDate < date) {
          dataItem.set('maturityDate', date)
        }
      })
    },
    'dealModel.referenceRate': function referenceRateChange(newValue) {
      let operations = this.operationDataSource.data()
      operations.forEach(oper => {
        this.recalculateGainLoss(newValue, oper)
      })
    },
    'dealModel.productId': {
      immediate: true,
      handler: function productChange(newValue, oldValue) {
        if (this.dealModel.dealId == 0) {
          this.initializeOperationGrid()
        } else {
          if (!oldValue) return
          window.setTimeout(() => {
            const leg = this.getLegByProduct(newValue)
            if ([product.Collar, product.FxSwap].includes(+newValue)) {
              return
            }
            this.operationDataSource.data().forEach(item => {
              item.set('legId', leg.legId)
              item.set('legCode', leg.legCode)
            })
          }, 100)
        }
      },
    },
    'dealModel.operations': function() {
      this.sync()
    },
    dealModel: function() {
      this.initializeOperationGrid()
    },
  },
  methods: {
    onRangeDatesSelected(dates) {
      const lastQuantity1 = this.getLastItemValue('amountCcy1') || 0
      const lastQuantity2 = this.getLastItemValue('amountCcy2') || 0
      const lastRate = this.getLastItemValue('rate') || 0
      const lastHedgeTypeCode =
        this.getLastItemValue('hedgeTypeCode') || this.dealModel.firstHedgeTypeCode
      const lastHedgeTypeId =
        this.getLastItemValue('hedgeTypeId') || this.dealModel.firstHedgeTypeId
      const lastFwd = this.getLastItemValue('fwd') || 0
      const data = this.operationDataSource.data()

      const coalesce = (expr, value) => (expr == null ? value : expr)

      dates.forEach(({startDate, endDate}) => {
        let defaultOperations = this.getDefaultOperationsByProduct(this.dealModel.productId)
        defaultOperations.forEach(row => {
          const defaultRow = data.find(r => r.legCode === row.legCode) || {}
          row.set('startDate', startDate)
          row.set('settlementDate', endDate)
          row.set('maturityDate', endDate)
          row.set('monthFlow', endDate)
          row.set('amountCcy1', coalesce(defaultRow.amountCcy1, lastQuantity1))
          row.set('rate', coalesce(defaultRow.rate, lastRate))
          row.set('amountCcy2', coalesce(defaultRow.amountCcy2, lastQuantity2))
          row.set('hedgeTypeCode', coalesce(defaultRow.hedgeTypeCode, lastHedgeTypeCode))
          row.set('hedgeTypeId', coalesce(defaultRow.hedgeTypeId, lastHedgeTypeId))
          row.set('fwd', coalesce(defaultRow.fwd, lastFwd))
          this.recalculateGainLoss(this.dealModel.referenceRate, row)

          this.operationDataSource.add(row)
        })
      })
    },
    validate() {
      if (
        this.operationDataSource.data().some(function(oper) {
          return oper.ccyPairId == null
        })
      ) {
        return ['Please assign a Ccy to the operation']
      }

      this.sync()
      return []
    },
    isEmpty() {
      return this.operationDataSource.data().length == 0
    },
    sync() {
      return this.operationDataSource.sync()
    },
    addRowClick() {
      if (this.dealModel.dealId == 0 && this.dealModel.operations.count == 0) {
        this.initializeOperationGrid()
      } else {
        let grid = this.$refs.operationGrid.kendoWidget()
        grid.addRow()
      }
    },
    cancelGridChanges() {
      this.operationDataSource.cancelChanges()
    },
    showAllClick(e) {
      this.showAllColumns = !this.showAllColumns
      const buttonIcon = e.sender.element.children()
      buttonIcon.toggleClass('k-i-eye')
      buttonIcon.toggleClass('k-i-filter-clear')
    },
    exportToExcel() {
      const hasChanges = this.operationDataSource.hasChanges()
      if (hasChanges) {
        confirm.openConfirmationWindow(
          'Has changes!',
          'You want to save the changes to continue?',
          () => {
            this.$emit('save-operation')

            this.operationDataSource.read().then(() => {
              this.exportToExcelFile()
            })
          },
        )
      } else {
        this.exportToExcelFile()
      }
    },
    exportToExcelFile() {
      const qs = params.fromObject({
        dealId: this.dealModel.dealId,
        clientId: this.dealModel.clientId,
        url: location.origin,
        t: this.tokenString,
      })
      setTimeout(function() {
        location.href = `/api/list/FxRate/exportContractTicket?${qs}`
      }, 182)
    },
    generateTicket() {
      const qs = params.fromObject({
        dealId: this.dealModel.dealId,
        clientId: this.dealModel.clientId,
        t: this.tokenString,
      })
      location.href = `/api/heroux/FxRate/generateTicket?${qs}`
    },
    openTicketForm() {
      const hasChanges = this.operationDataSource.hasChanges()
      if (hasChanges) {
        confirm.openConfirmationWindow(
          'Has changes!',
          'You want to save the changes to continue?',
          () => {
            this.$emit('save-operation')

            this.operationDataSource.read().then(() => {
              this.contractFormVisible = true
            })
          },
        )
      } else {
        this.contractFormVisible = true
      }
    },
    getLastItemValue: function(key) {
      if (!this.operationDataSource) return
      let [lastItem] = this.operationDataSource.data().slice(-1)
      if (lastItem) {
        return lastItem[key]
      }
      return ''
    },
    getIsBuying() {
      const lastIsBuying = this.getLastItemValue('isBuying')
      if (lastIsBuying === '') {
        if (this.hasOrders) {
          const orderGrid = kendo.jQuery('#dealOrderGrid').getKendoGrid()
          if (!orderGrid) return lastIsBuying
          const ds = orderGrid.dataSource
          const order = ds.get(0)
          if (!order) {
            return this.dealModel.order.isBuying
          }
          return order.isBuying
        }
        return this.dealModel.defaultDealOptions.isBuying
      }

      return lastIsBuying
    },
    getDefaultOperationsByProduct(productId) {
      const clientBuy = this.getIsBuying()
      let spot, call, put, fwd

      let base = this.operationDataSource._createNewModel()

      switch (+productId) {
        case product.Fwd:
        case product.Avg:
        case product.Window:
          base.legCode = 'FWD'
          base.legId = 1
          base.isBuying = clientBuy
          this.setupHedgeType(base)
          base.buySell = clientBuy ? 'Buy' : 'Sell'
          base.buySell2 = !clientBuy ? 'Buy' : 'Sell'
          return [base]
        case product.Collar:
          put = this.operationDataSource._createNewModel()
          put.legCode = 'PUT'
          put.legId = 4
          put.isBuying = !clientBuy
          this.setupHedgeType(put)
          put.buySell = !clientBuy ? 'Buy' : 'Sell'
          put.buySell2 = clientBuy ? 'Buy' : 'Sell'

          call = this.operationDataSource._createNewModel()
          call.legCode = 'CALL'
          call.legId = 3
          call.isBuying = clientBuy
          this.setupHedgeType(call)
          call.buySell = clientBuy ? 'Buy' : 'Sell'
          call.buySell2 = !clientBuy ? 'Buy' : 'Sell'
          return [put, call]
        case product.Spot:
          base.legCode = 'SPOT'
          base.legId = 2
          base.isBuying = clientBuy
          this.setupHedgeType(base)
          base.buySell = clientBuy ? 'Buy' : 'Sell'
          base.buySell2 = !clientBuy ? 'Buy' : 'Sell'
          return [base]
        case product.Exotic:
          var call1 = this.operationDataSource._createNewModel()
          call1.legCode = 'CALL'
          call1.legId = 3
          call1.isBuying = !clientBuy
          this.setupHedgeType(call1)
          call1.buySell = !clientBuy ? 'Buy' : 'Sell'
          call1.buySell2 = clientBuy ? 'Buy' : 'Sell'

          call = this.operationDataSource._createNewModel()
          call.legCode = 'CALL'
          call.legId = 3
          call.isBuying = clientBuy
          this.setupHedgeType(call)
          call.buySell = clientBuy ? 'Buy' : 'Sell'
          call.buySell2 = !clientBuy ? 'Buy' : 'Sell'
          return [call1, call]
        case product.FxSwap:
          spot = this.operationDataSource._createNewModel()
          spot.legCode = 'SPOT'
          spot.legId = 2
          spot.isBuying = !clientBuy
          this.setupHedgeType(spot)
          spot.buySell = !clientBuy ? 'Buy' : 'Sell'
          spot.buySell2 = clientBuy ? 'Buy' : 'Sell'

          fwd = base
          fwd.legCode = 'FWD'
          fwd.legId = 1
          fwd.isBuying = clientBuy
          this.setupHedgeType(fwd)
          fwd.buySell = clientBuy ? 'Buy' : 'Sell'
          fwd.buySell2 = !clientBuy ? 'Buy' : 'Sell'
          return [spot, fwd]
        case product.Call:
          base.legCode = 'CALL'
          base.legId = 3
          return [base]
        case product.Put:
          base.legCode = 'PUT'
          base.legId = 4
          return [base]
        case product.Future:
          base.legCode = 'FUT'
          base.legId = 6
          this.setupHedgeType(base)
          base.buySell = clientBuy ? 'Buy' : 'Sell'
          base.buySell2 = !clientBuy ? 'Buy' : 'Sell'
          return [base]
        default:
          return null
      }
    },
    showStartDate() {
      if (this.showAllColumns) return true
      return [product.Avg, product.Window].indexOf(+this.dealModel.productId) >= 0
    },
    showSettlementCcy() {
      if (this.showAllColumns) return true
      return [product.Avg].indexOf(+this.dealModel.productId) >= 0
    },
    showSettlementMethod() {
      if (this.showAllColumns) return true
      let settledMethodProducts = this.optionProducts.slice()
      settledMethodProducts.push(product.Avg)
      return settledMethodProducts.indexOf(+this.dealModel.productId) >= 0
    },
    showOptionColumns() {
      if (this.showAllColumns) return true
      return this.optionProducts.indexOf(+this.dealModel.productId) >= 0
    },
    setupHedgeType(item) {
      const rowIsBuying = item.get('IsBuying')

      if (this.optionProducts.indexOf(this.dealModel.productId) >= 0 && !rowIsBuying) {
        item.set('hedgeTypeId', this.dealModel.firstHedgeTypeId)
        item.set('hedgeTypeCode', this.dealModel.firstHedgeTypeCode)
        return
      }

      item.set('hedgeTypeId', this.dealModel.defaultHedgeTypeId)
      item.set('hedgeTypeCode', this.dealModel.defaultHedgeTypeCode)
    },
    recalculateGainLoss(referenceRate, operation) {
      var gainLoss = operation.isBuying
        ? (referenceRate + operation.fwd / 10000 - operation.rate) * 10000
        : (operation.rate - (referenceRate + operation.fwd / 10000)) * 10000

      if (!isFinite(gainLoss)) return

      operation.set('gainLoss', gainLoss)
    },
    initializeOperationGrid() {
      if (this.dealModel.dealId == 0) {
        this.operationDataSource.data().splice(0, this.operationDataSource.data().length)
        let defaultOperations = this.getDefaultOperationsByProduct(this.dealModel.productId)
        defaultOperations.forEach(item => {
          this.operationDataSource.add(item)
        })
      }
    },
    openScheduleDeliveryModal(e) {
      if (/deliveryScheduleLinkModal/.test(e.target.className)) {
        const operationId = $(e.target).data('operationid')
        this.operationId = operationId
        this.scheduleModalVisible = true
        e.stopPropagation()
      }
      if (/exercisedCheckbox/.test(e.target.className)) {
        var dataItem = getDataItem(e.target)
        var isExercised = dataItem.exercised
        dataItem.set('exercised', !isExercised)
      }
    },
    closeScheduleDeliveryModal() {
      this.operationId = null
      this.scheduleModalVisible = false
    },
    openRangeDealsDialog: function() {
      this.addRangeDealsDialog = !this.addRangeDealsDialog
    },
    getDefaultDividing() {
      const lastDividing = this.getLastItemValue('ccyDividing')
      if (lastDividing === '') {
        if (this.dealModel.order != null) {
          return this.dealModel.order.isDividingCcy
        }
        return this.dealModel.defaultDealOptions.isDividingCcy
      }

      return lastDividing
    },
    onAddOperationsFromOrder({dates, order}) {
      const isDividing = this.getDefaultDividing()
      const {amount, orderRate, ccyPairId, ccy1, ccy2, legId, legCode} = order
      const amount2 = amount * (isDividing ? 1 / orderRate : orderRate)

      dates.forEach(({startDate, endDate}) => {
        let defaultOperations = this.getDefaultOperationsByProduct(this.dealModel.productId)

        defaultOperations.forEach(row => {
          row.set('ccyPairId', ccyPairId)
          row.set('legId', legId)
          row.set('legCode', legCode)
          row.set('foreignCcy', ccy1)
          row.set('baseCcy', ccy2)
          row.set('startDate', startDate)
          row.set('settlementDate', endDate)
          row.set('maturityDate', endDate)
          row.set('monthFlow', endDate)
          row.set('amountCcy1', amount)
          row.set('rate', orderRate)
          row.set('amountCcy2', amount2)

          this.operationDataSource.add(row)
        })
      })
    },
    recalculateGridHeight() {
      const gridEl = this.$refs.operationGrid.$el
      setTimeout(() => (this.operationsGridOffsetTop = gridEl.offsetTop), 100)
    },
    getLegByProduct(productId) {
      const base = {}
      const id = +productId
      switch (id) {
        case product.Fwd:
        case product.Avg:
        case product.Window:
          base.legCode = 'FWD'
          base.legId = 1
          return base
        case product.Collar:
          base.legCode = 'PUT'
          base.legId = 4
          return base
        case product.Spot:
          base.legCode = 'SPOT'
          base.legId = 2
          return base
        case product.Exotic:
          base.legCode = 'CALL'
          base.legId = 3
          return base
        case product.FxSwap:
          base.legCode = 'SPOT'
          base.legId = 2
          return base
        case product.Call:
          base.legCode = 'CALL'
          base.legId = 3
          return base
        case product.Put:
          base.legCode = 'PUT'
          base.legId = 4
          return base
        case product.Future:
          base.legCode = 'FUT'
          base.legId = 6
          return base
        default:
          base.legCode = 'FWD'
          base.legId = 1
          return base
      }
    },
  },
  computed: {
    ...mapGetters({tokenString: 'authentication/tokenString'}),
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({selectedClient: 'authentication/selectedClient'}),
    ...mapGetters({selectedMarketId: 'app/marketId'}),

    clientName() {
      return this.selectedClient.name
    },
    editableSettings: function() {
      return {
        mode: 'incell',
        update: this.editable,
        destroy: this.editable,
        confirmation: false,
        createAt: 'bottom',
      }
    },
    gridColumns: function() {
      let columns = [
        {
          field: 'dealCode',
          title: 'Trade Id',
          width: 82,
          locked: true,
          template: function(item) {
            var template =
              item.legCode === 'FWD'
                ? '<a title="Delivery Schedule" data-operationid="' +
                  item.operationId +
                  '" href="javascript:;" class="deliveryScheduleLinkModal k-cellLink">' +
                  item.dealCode +
                  '</a>'
                : '<span>' + item.dealCode + '</span>'
            return template
          },
        },
        {
          field: 'legCode',
          title: 'Leg',
          width: 90,
          editor: (function(vm) {
            return function(container, options) {
              return jsonDropDownEditor(
                'legId',
                'legCode',
                `/api/list/deal/getLegs?dealTypeId=${vm.dealTypeId}&legId=${options.model.legId}`,
                {
                  autoWidth: true,
                  dataBound: function() {
                    this.trigger('change')
                  },
                },
                false,
              ).call(this, container, options)
            }
          })(this),
        },
        {
          field: 'maturityDate',
          title: 'Maturity',
          width: 100,
          format: '{0:yyyy-MM-dd}',
          attributes: {style: 'text-align: right;'},
          editor: (container, options) => {
            return datePickerEditor(
              {
                min: this.dealModel.tradeDate,
                format: 'yyyy-MM-dd',
                change: function(e) {
                  var dataItem = getDataItem(e.sender.element)
                  dataItem.set('settlementDate', e.sender.value())
                  dataItem.set('monthFlow', e.sender.value())
                  dataItem.set('startDate', e.sender.value())
                },
              },
              true,
            )(container, options)
          },
        },
        {
          field: 'startDate',
          title: 'Start Date',
          width: 100,
          format: '{0:yyyy-MM-dd}',
          attributes: {style: 'text-align: right;'},
          editor: (container, options) => {
            return datePickerEditor(
              {
                min: this.dealModel.tradeDate,
                max: options.model.maturityDate,
                format: 'yyyy-MM-dd',
              },
              true,
            ).call(this, container, options)
          },
          hidden: !this.showStartDate(),
        },
        {
          field: 'settlementDate',
          title: 'Settlement',
          width: 100,
          format: '{0:yyyy-MM-dd}',
          hidden: !this.showStartDate(),
          attributes: {style: 'text-align: right;'},
          editor: (container, options) => {
            return datePickerEditor(
              {
                min: this.dealModel.tradeDate,
                format: 'yyyy-MM-dd',
                change: function(e) {
                  var dataItem = getDataItem(e.sender.element)
                  dataItem.set('monthFlow', e.sender.value())
                },
              },
              true,
            )(container, options)
          },
        },
        {
          field: 'isBuying',
          title: 'Buy / Sell',
          width: 80,
          editor: isBuyingEditor,
          template: function(dataItem) {
            return '<span>' + (dataItem.isBuying ? 'Buy' : 'Sell') + '</span>'
          },
        },
        {
          field: 'amountCcy1',
          title: 'Quantity Com.Ccy',
          width: 110,
          editor: numericEditor({
            decimals: 2,
            format: '{0:n0}',
            spinners: false,
            step: 0,
          }),
          attributes: {style: 'text-align: right;'},
          template: function(dataItem) {
            return '<span>' + kendo.toString(Math.abs(dataItem.amountCcy1), 'n0') + '</span>'
          },
        },
        {
          field: 'foreignCcy',
          title: 'Com.Ccy',
          width: 80,
          editor: (function(vm) {
            return function(container, options) {
              var $row = $('tr[data-uid=' + options.model.uid + ']'),
                rowIndex = $row.index()

              if (options.model.ccyPairId && rowIndex > 0) {
                return readOnlyEditor.apply(this, arguments)
              }

              return jsonDropDownEditor(
                'foreignCcy',
                'foreignCcy',
                '/api/list/deal/getForeignCurrencies?clientId=' +
                  vm.clientId +
                  '&marketId=' +
                  (vm.dealModel.dealId == 0 && vm.selectedMarketId <= 0
                    ? -1
                    : vm.dealModel.marketId),
                false,
              ).call(this, container, options)
            }
          })(this),
        },
        {
          field: 'rate',
          title: 'Contract Rate',
          width: 90,
          editor: numericEditor({
            decimals: 16,
            format: '{0:n4}',
            spinners: false,
            step: 0,
          }),
          attributes: {style: 'text-align: right;'},
          template: function(dataItem) {
            return '<span>' + kendo.toString(Math.abs(dataItem.rate), 'n6') + '</span>'
          },
        },
        {
          field: 'buySell2',
          title: 'Buy / Sell',
          width: 80,
          editor: readOnlyEditor,
        },
        {
          field: 'amountCcy2',
          title: 'Amount Ccy',
          width: 110,
          format: '{0:n0}',
          editor: numericEditor({
            decimals: 2,
            format: '{0:n0}',
            spinners: false,
            step: 0,
          }),
        },
        {
          field: 'baseCcy',
          title: 'Ccy',
          width: 80,
          editor: function(container, options) {
            const $row = $('tr[data-uid=' + options.model.uid + ']'),
              rowIndex = $row.index()

            if (options.model.ccyPairId && rowIndex > 0) {
              return readOnlyEditor.apply(this, arguments)
            }

            const marketId =
              this.dealModel.dealId == 0 && this.selectedMarketId <= 0
                ? -1
                : this.dealModel.marketId

            return baseCcyEditor(this.clientId, marketId, (dataItem, e) => {
              const ccyModel = e.sender.dataItem()
              if (this.dealModel.marketId != ccyModel.marketId) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dealModel.marketId = ccyModel.marketId
              }

              this.operationDataSource.view().forEach(function(el) {
                if (el.ccyPairId !== dataItem.ccyPairId) {
                  el.set('ccyPairId', dataItem.ccyPairId)
                  el.set('foreignCcy', dataItem.foreignCcy)
                  el.set('baseCcy', dataItem.baseCcy)
                  el.set('ccyDividing', dataItem.ccyDividing)
                  el.set('settlementCcy', dataItem.baseCcy)
                }
              })
            }).call(this, container, options)
          }.bind(this),
        },
        {
          field: 'fwd',
          title: 'Fwd Points',
          width: 100,
          format: '{0:n2}',
          editor: numericEditor({decimals: 16, format: '{0:n2}', spinners: false}),
          attributes: {style: 'text-align: right;'},
        },
        {
          field: 'monthFlow',
          title: 'Hedge Month',
          width: 100,
          format: '{0:yyyy-MM}',
          attributes: {style: 'text-align: right;'},
          editor: datePickerEditor({format: 'yyyy-MM', start: 'year', depth: 'year'}, true),
        },
        {
          field: 'hedgeTypeCode',
          title: 'Hedge Type',
          width: 90,
          editor: jsonDropDownEditor(
            'hedgeTypeId',
            'hedgeTypeCode',
            `/api/list/deal/getHedgeTypes?clientId=${this.clientId}&dealId=${
              this.dealModel.dealId
            }&marketId=${this.marketId}`,
            {
              dataBound: function() {
                this.trigger('change')
              },
            },
            true,
          ),
        },
        {
          field: 'reference',
          title: 'Ref.',
          width: 132,
        },
        {
          field: 'otherReference',
          title: 'Others/Premium',
          width: 132,
        },
        {
          field: 'exercised',
          title: 'Exerc.',
          width: 80,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: center;'},
          template:
            "<input type='checkbox' class='exercisedCheckbox' data-bind='checked: exercised' #= exercised ? checked='checked' : '' #/>",
          editor:
            "<input type='checkbox' data-bind='checked: exercised' #= exercised ? checked='checked' : '' #/>",
        },
        {
          field: 'gainLoss',
          title: 'Gain / Loss',
          width: 100,
          editor: numericEditor({
            decimals: 6,
            format: '{0:n2}',
            spinners: false,
            step: 0,
          }),
          attributes: {style: 'text-align: right;'},
          template: function(dataItem) {
            if (dataItem.gainLoss == null) return ''
            return '<span>' + kendo.toString(dataItem.gainLoss, 'n2') + '</span>'
          },
          hidden: !this.showOptionColumns(),
        },
        {
          field: 'economySpread',
          title: 'Savings FINMETRIX',
          width: 100,
          editor: numericEditor({
            decimals: 6,
            format: '{0:n2}',
            spinners: false,
            step: 0,
          }),
          attributes: {style: 'text-align: right;'},
          template: function(dataItem) {
            return '<span>' + kendo.toString(dataItem.economySpread, 'n2') + '</span>'
          },
        },
        {
          field: 'optionTypeName',
          title: 'Opt. Type',
          width: 110,
          editor: jsonDropDownEditor(
            'optionTypeId',
            'optionTypeName',
            '/api/list/deal/getOptionTypes',
            {},
            true,
          ),
          hidden: !this.showOptionColumns(),
        },
        {
          field: 'otherBarrier',
          title: 'Other barrier',
          width: 100,
          editor: numericEditor({
            decimals: 16,
            format: '{0:n6}',
            spinners: false,
            step: 0,
          }),
          attributes: {style: 'text-align: right;'},
          template: function(dataItem) {
            if (dataItem.otherBarrier == null) return ''
            return '<span>' + kendo.toString(Math.abs(dataItem.otherBarrier), 'n6') + '</span>'
          },
          hidden: !this.showOptionColumns(),
        },
        {
          field: 'settlementMethodName',
          title: 'Settlement Method',
          width: 110,
          editor: jsonDropDownEditor(
            'settlementMethodId',
            'settlementMethodName',
            '/api/list/deal/getSettlementMethods',
            {
              dataBound: function() {
                this.trigger('change')
              },
            },
            true,
          ),
          hidden: !this.showSettlementMethod(),
        },
        {
          field: 'settlementCcy',
          title: 'Settlement Ccy',
          width: 82,
          editor: function(container, options) {
            return jsonDropDownEditor(
              'settlementCcy',
              'settlementCcy',
              `/api/list/deal/getSettlementCcy?ccyPairId=${options.model.ccyPairId}`,
              {
                dataBound: function() {
                  this.trigger('change')
                },
              },
              false,
            ).call(this, container, options)
          },
          hidden: !this.showSettlementCcy(),
        },
        {
          field: 'comment',
          title: 'Comment.',
          width: 182,
        },
        {
          command: 'destroy',
          title: '&nbsp;',
          width: 64,
          hidden: !this.editable,
        },
        {},
      ]
      return columns
    },
    operationDataSource() {
      if (!this.dealModel.operations) return null
      return new kendo.data.DataSource({
        transport: {
          read: e => {
            e.success(this.dealModel.operations)
          },
          update: e => {
            const [item] = this.dealModel.operations.filter(d => {
              return d.operationIdentifier == e.data.operationIdentifier
            })

            Object.assign(item, e.data)

            e.success()
          },
          create: e => {
            var item = e.data
            item.operationIdentifier = 'Oper' + Math.floor(Math.random() * 1e5)

            e.success(item)
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dealModel.operations.push(item)
          },
          destroy: e => {
            const deletedOperId = e.data.operationIdentifier
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dealModel.operations = this.dealModel.operations.filter(d => {
              return d.operationIdentifier != deletedOperId
            })
            e.success(e.data)
          },
        },
        schema: {
          model: {
            id: 'operationIdentifier',
            fields: {
              operationIdentifier: {
                type: 'string',
              },
              operationId: {type: 'number'},
              fxId: {type: 'number'},
              hedgeTypeCode: {
                type: 'string',
                defaultValue: () => {
                  if (this.hasOrders) {
                    return this.dealModel.defaultHedgeTypeCode
                  }

                  var isBuying = this.dealModel.defaultDealOptions.isBuying
                  var hedgeTypeCode =
                    this.getLastItemValue('hedgeTypeCode') ||
                    (isBuying
                      ? this.dealModel.firstHedgeTypeCode
                      : this.dealModel.defaultHedgeTypeCode)
                  return hedgeTypeCode
                },
              },
              hedgeTypeId: {
                type: 'number',
                defaultValue: () => {
                  if (this.hasOrders) {
                    return this.dealModel.defaultHedgeTypeId
                  }

                  var isBuying = this.dealModel.defaultDealOptions.isBuying
                  var hedgeTypeId =
                    this.getLastItemValue('hedgeTypeId') ||
                    (isBuying ? this.dealModel.firstHedgeTypeId : this.dealModel.defaultHedgeTypeId)
                  return hedgeTypeId
                },
              },
              dealCode: {type: 'string', editable: false},
              reference: {type: 'string'},
              legCode: {
                type: 'string',
                defaultValue: () => {
                  if (this.addTradeByOrder()) {
                    return this.dealModel.order.legCode
                  }
                  return this.getLegByProduct(this.dealModel.productId).legCode
                },
              },
              legId: {
                type: 'number',
                defaultValue: () => {
                  if (this.addTradeByOrder()) {
                    return this.dealModel.order.legId
                  }
                  return this.getLegByProduct(this.dealModel.productId).legId
                },
              },
              startDate: {
                type: 'date',
                defaultValue: () => {
                  return kendo.parseDate(this.dealModel.tradeDate)
                },
              },
              settlementDate: {
                type: 'date',
                defaultValue: () => {
                  return kendo.parseDate(this.dealModel.tradeDate)
                },
                validation: {
                  required: true,
                  minMaturityDateValidation: function(input) {
                    if (input.is("[name='settlementDate']")) {
                      var datepicker = input.getKendoDatePicker()
                      if (datepicker.value() === null) {
                        input.attr(
                          'data-minMaturityDateValidation-msg',
                          'Settlement Date is required',
                        )
                        return false
                      }
                    }
                    return true
                  },
                },
              },
              maturityDate: {
                type: 'date',
                defaultValue: () => {
                  return kendo.parseDate(this.dealModel.tradeDate)
                },
                validation: {
                  required: true,
                  minMaturityDateValidation: function(input) {
                    if (input.is("[name='maturityDate']")) {
                      var datepicker = input.getKendoDatePicker()
                      if (datepicker.value() === null) {
                        input.attr(
                          'data-minMaturityDateValidation-msg',
                          'Settlement Date is required',
                        )
                        return false
                      }
                    }
                    return true
                  },
                },
              },
              isBuying: {
                type: 'boolean',
                defaultValue: () => {
                  return this.getIsBuying()
                },
              },
              ccyPairId: {
                type: 'number',
                defaultValue: () => {
                  if (this.addTradeByOrder()) {
                    return this.dealModel.order.ccyPairId
                  }
                  return (
                    this.getLastItemValue('ccyPairId') ||
                    this.dealModel.defaultDealOptions.ccyPairId
                  )
                },
              },
              ccyDividing: {
                type: 'boolean',
                defaultValue: () => {
                  return this.getDefaultDividing()
                },
              },
              buySell: {
                type: 'string',
                defaultValue: () => {
                  return (
                    this.getLastItemValue('buySell') || this.dealModel.defaultDealOptions.buySell
                  )
                },
              },
              amountCcy1: {
                type: 'number',
                defaultValue: () => {
                  if (this.addTradeByOrder()) {
                    return this.dealModel.order.amount
                  }
                  return 0
                },
              },
              foreignCcy: {
                type: 'string',
                defaultValue: () => {
                  if (this.addTradeByOrder()) {
                    return this.dealModel.order.ccy1
                  }
                  return (
                    this.getLastItemValue('foreignCcy') ||
                    this.dealModel.defaultDealOptions.foreignCcy
                  )
                },
              },
              buySell2: {
                type: 'string',
                defaultValue: () => {
                  return (
                    this.getLastItemValue('buySell2') || this.dealModel.defaultDealOptions.buySell2
                  )
                },
              },
              amountCcy2: {type: 'number'},
              baseCcy: {
                type: 'string',
                defaultValue: () => {
                  if (this.addTradeByOrder()) {
                    return this.dealModel.order.ccy2
                  }
                  return (
                    this.getLastItemValue('baseCcy') || this.dealModel.defaultDealOptions.baseCcy
                  )
                },
              },
              rate: {
                type: 'number',
                defaultValue: () => {
                  return this.getLastItemValue('rate') || 0
                },
              },
              monthFlow: {
                type: 'date',
                defaultValue: () => {
                  return kendo.parseDate(this.dealModel.tradeDate)
                },
              },
              otherBarrier: {type: 'number'},
              fwd: {type: 'number', defaultValue: 0},
              exercised: {type: 'boolean', defaultValue: false},
              settlementCcy: {
                type: 'string',
                defaultValue: () => {
                  return (
                    this.getLastItemValue('baseCcy') || this.dealModel.defaultDealOptions.baseCcy
                  )
                },
              },
              optionTypeId: {
                type: 'number',
                defaultValue: () => {
                  var productId = this.dealModel.productId
                  var optionTypeId = 0
                  if (this.optionProducts.indexOf(productId) >= 0) optionTypeId = 1

                  return optionTypeId
                },
              },
              optionTypeName: {
                type: 'string',
                defaultValue: () => {
                  var productId = this.dealModel.productId
                  var optionTypeName = 'N/A'
                  if (this.optionProducts.indexOf(productId) >= 0) optionTypeName = 'Euro'

                  return optionTypeName
                },
              },
              settlementMethodId: {
                type: 'number',
                defaultValue: () => {
                  var productId = this.dealModel.ProductId
                  var settlementMethodId = 0
                  if (productId === product.Avg) settlementMethodId = 1

                  return settlementMethodId
                },
              },
              settlementMethodName: {
                type: 'string',
                defaultValue: () => {
                  var productId = this.dealModel.productId
                  var settlementMethod = 'Standard'
                  if (productId === product.Avg) settlementMethod = 'Average'

                  return settlementMethod
                },
              },
              gainLoss: {type: 'number'},
              economySpread: {type: 'number'},
              comment: {type: 'string'},
            },
          },
        },
        change: function(e) {
          if (e.action === 'add') {
            const lastStartDate = e.items.slice(-1)[0].startDate
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.lastStartDate = lastStartDate
          } else if (e.action !== 'itemChange') {
            const [lastItem] = e.sender.data().slice(-1)
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.lastStartDate = lastItem ? lastItem.startDate : this.dealModel.tradeDate
          }

          if (e.action != 'itemchange') return
          const item = e.items[0]

          let rate, amountCcy2

          switch (e.field) {
            case 'legId':
              {
                let legId = item.legId
                item.set('optionTypeName', '')
                if (legId == 3 || legId == 4) {
                  item.set('optionTypeId', 8)
                  item.set('optionTypeName', 'Standard')
                } else if (legId == 6) {
                  item.set('optionTypeId', 0)
                  item.set('optionTypeName', 'N/A')
                }
              }
              break
            case 'foreignCcy':
              item.set('baseCcy', '')
              item.set('settlementCcy', '')
              // eslint-disable-next-line no-case-declarations
              const marketId = this.dealModel.dealId == 0 ? -1 : this.dealModel.marketId
              // eslint-disable-next-line no-case-declarations
              let allCcyData = new kendo.data.DataSource({
                transport: {
                  read: {
                    url: `/api/list/deal/getAllCurrencies?clientId=${
                      this.clientId
                    }&marketId=${marketId}`,
                    contentType: 'application/json',
                    type: 'GET',
                  },
                },
              })
              allCcyData.read().then(() => {
                allCcyData.filter([{field: 'foreignCcy', operator: 'eq', value: item.foreignCcy}])
                const ccyData = allCcyData.view()[0]

                this.operationDataSource.view().forEach(function(el) {
                  if (el.ccyPairId !== ccyData.ccyPairId) {
                    el.set('ccyPairId', ccyData.ccyPairId)
                    el.set('foreignCcy', ccyData.foreignCcy)
                    el.set('baseCcy', ccyData.baseCcy)
                    el.set('ccyDividing', ccyData.isDividing)
                    el.set('settlementCcy', ccyData.baseCcy)
                  }
                })
              })
              break
            case 'baseCcy':
              //if (item.BaseCcy == '') return;

              //this.filter(function (el) {
              //    return el.OperationIdentifier != item.OperationIdentifier;
              //}).forEach(function (el) {
              //    if (el.CcyPairId !== item.CcyPairId) {
              //        el.set("CcyPairId", item.CcyPairId);
              //        el.set("ForeignCcy", item.ForeignCcy);
              //        el.set("BaseCcy", item.BaseCcy);
              //    }
              //});
              item.set('settlementCcy', item.baseCcy)
              break
            case 'amountCcy1':
            case 'rate':
            case 'ccyDividing':
              rate = item.ccyDividing ? 1 / item.rate : item.rate
              amountCcy2 = item.amountCcy1 * rate
              if (Math.abs(amountCcy2 - item.amountCcy2) > 1e-10) {
                item.set('amountCcy2', amountCcy2)
              }
              this.recalculateGainLoss(this.dealModel.referenceRate, item)
              break
            case 'amountCcy2':
              rate = item.ccyDividing
                ? item.amountCcy1 / item.amountCcy2
                : item.amountCcy2 / item.amountCcy1
              if (Math.abs(rate - item.rate) > 1e-10) {
                item.set('rate', rate)
              }
              break
            case 'fwd':
            case 'isBuying':
              this.recalculateGainLoss(this.dealModel.referenceRate, item)
              break
          }
        }.bind(this),
      })
    },
    operationsGridHeight() {
      if (this.hasOrders) {
        return `calc(100vh - ${this.operationsGridOffsetTop + 190}px)`
      }
      return `calc(100vh - ${this.operationsGridOffsetTop + 185}px)`
    },
    initialStartDate() {
      const [lastOperation] = this.dealModel.operations.slice(-1)
      const lastStartDate = this.lastStartDate

      const date = lastOperation
        ? max(lastOperation.startDate, lastStartDate)
        : max(new Date(), lastStartDate)

      return addMonths(date, 1)
    },
  },
  components: {
    PageToolbar,
    ScheduleDeliveryForm,
    RangeDeals,
    TicketForm,
  },
}
</script>
